import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * Safe decorator che previene doppia registrazione
 */
function safeCustomElement(tagName: string) {
  return function <T extends CustomElementConstructor>(constructor: T) {
    if (!customElements.get(tagName)) {
      try {
        customElements.define(tagName, constructor);
        console.debug(`✅ Registrato: ${tagName}`);
      } catch (error) {
        console.warn(`⚠️ Errore registrazione ${tagName}:`, error);
      }
    } else {
      console.debug(`⏭️ ${tagName} già registrato`);
    }
    return constructor;
  };
}

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  heading?: string;
  message: string;
  variant?: ToastVariant;
  autohide?: number;
}

interface VariantStyle {
  border: string;
  background: string;
}

const VARIANT_STYLES: Record<ToastVariant, VariantStyle> = {
  info: {
    border: 'var(--pm-toast-border-info, 1px solid rgba(13, 110, 253, 0.4))',
    background: 'var(--pm-toast-bg-info, rgba(13, 110, 253, 0.9))'
  },
  success: {
    border: 'var(--pm-toast-border-success, 1px solid rgba(25, 135, 84, 0.4))',
    background: 'var(--pm-toast-bg-success, rgba(25, 135, 84, 0.9))'
  },
  warning: {
    border: 'var(--pm-toast-border-warning, 1px solid rgba(255, 193, 7, 0.4))',
    background: 'var(--pm-toast-bg-warning, rgba(255, 193, 7, 0.92))'
  },
  danger: {
    border: 'var(--pm-toast-border-danger, 1px solid rgba(220, 53, 69, 0.4))',
    background: 'var(--pm-toast-bg-danger, rgba(220, 53, 69, 0.92))'
  }
};

@safeCustomElement('pm-toast')
export class PmToast extends LitElement {
  @property({ type: String, reflect: true }) variant: ToastVariant = 'info';
  @property({ type: String }) heading: string = '';
  @property({ type: String }) message: string = '';
  @property({ type: Boolean, reflect: true }) open: boolean = false;
  @property({ type: Number, attribute: 'autohide' }) autohide: number = 0;
  
  @state() private _hideTimer?: number | null;

  static styles = css`
    :host {
      position: fixed;
      bottom: 2.5rem;
      right: 2rem;
      display: block;
      max-width: min(380px, calc(100% - 3rem));
      color: var(--pm-toast-text-color, #fff);
      border-radius: 1rem;
      padding: 1.25rem;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.25);
      opacity: 0;
      transform: translateY(20px) scale(0.96);
      transform-origin: bottom right;
      pointer-events: none;
      transition: opacity 150ms ease, transform 200ms ease;
      z-index: 1080;
      background: rgba(15, 23, 42, 0.85);
      border: 1px solid rgba(255, 255, 255, 0.08);
      font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
      line-height: 1.4;
    }

    :host([open]) {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0) scale(1);
    }

    .heading {
      font-size: 1rem;
      font-weight: 600;
      margin: 0 0 0.35rem;
      letter-spacing: 0.01em;
    }

    .message {
      font-size: 0.95rem;
      margin: 0;
    }

    button.dismiss {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      padding: 0.35rem;
      border-radius: 999px;
      transition: background 120ms ease;
    }

    button.dismiss:focus {
      outline: 2px solid rgba(255, 255, 255, 0.75);
      outline-offset: 2px;
    }

    button.dismiss:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    @media (max-width: 768px) {
      :host {
        left: 50%;
        right: auto;
        bottom: 1.5rem;
        transform: translate(-50%, 20px) scale(0.96);
        transform-origin: bottom center;
        max-width: min(480px, calc(100% - 2rem));
      }

      :host([open]) {
        transform: translate(-50%, 0) scale(1);
      }
    }
  `;

  constructor() {
    super();
    this.heading = '';
    this.message = '';
    this.variant = 'info';
    this.open = false;
    this.autohide = 5500;
    this._hideTimer = null;
  }

  render() {
    return html`
      <button
        class="dismiss"
        type="button"
        @click=${this.hide}
        aria-label="Chiudi notifica"
      >
        &times;
      </button>
      ${this.heading
        ? html`<h6 class="heading">${this.heading}</h6>`
        : null}
      <p class="message">${this.message}</p>
    `;
  }

  updated(changedProps: PropertyValues) {
    if (changedProps.has('variant')) {
      this.#applyVariantStyles();
    }

    if (changedProps.has('open')) {
      if (this.open) {
        this.#startAutohideTimer();
        this.dispatchEvent(new CustomEvent('pm-toast:shown', { bubbles: true, composed: true }));
      } else {
        this.#clearAutohideTimer();
        this.dispatchEvent(new CustomEvent('pm-toast:hidden', { bubbles: true, composed: true }));
      }
    }
  }

  disconnectedCallback() {
    this.#clearAutohideTimer();
    super.disconnectedCallback();
  }

  show(options: ToastOptions | string = '') {
    if (typeof options === 'string') {
      this.message = options;
    } else {
      this.heading = options.heading ?? this.heading;
      this.message = options.message ?? this.message;
      this.variant = options.variant ?? this.variant;
      if (typeof options.autohide === 'number') {
        this.autohide = options.autohide;
      }
    }

    if (!this.message) {
      console.warn('pm-toast: nessun messaggio da mostrare');
      return;
    }

    this.open = true;
  }

  hide = () => {
    this.open = false;
  };

  toggle(options?: ToastOptions | string) {
    if (this.open) {
      this.hide();
    } else {
      this.show(options);
    }
  }

  #applyVariantStyles() {
    const style = VARIANT_STYLES[this.variant] ?? VARIANT_STYLES.info;
    this.style.background = style.background;
    this.style.border = style.border;
  }

  #startAutohideTimer() {
    this.#clearAutohideTimer();
    if (this.autohide && this.autohide > 0) {
      this._hideTimer = setTimeout(() => {
        this.hide();
      }, this.autohide) as any;
    }
  }

  #clearAutohideTimer() {
    if (this._hideTimer) {
      clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }
  }
}

// Esporta per uso in altri moduli  
export default PmToast;
